import React from 'react';
import { RotateCcw } from 'lucide-react';

export type QuestionDifficulty = 'easy' | 'medium' | 'hard';

interface DifficultyBadgeProps {
  difficulty?: QuestionDifficulty | string;
  isReview?: boolean;
  reviewLabel?: string;
  fontSize?: number;
  className?: string;
  guideTargetId?: string;
}

const difficultyConfig: Record<QuestionDifficulty, { label: string; filled: number }> = {
  easy: { label: 'آسان', filled: 1 },
  medium: { label: 'متوسط', filled: 2 },
  hard: { label: 'سخت', filled: 3 },
};

const normalizeDifficulty = (difficulty?: string): QuestionDifficulty => {
  if (difficulty === 'easy' || difficulty === 'hard') return difficulty;
  return 'medium';
};

/** A compact speedometer with a difficulty-dependent needle. */
const DifficultyIcon = ({ filled }: { filled: number }) => (
  <svg width="22" height="18" viewBox="0 0 24 18" aria-hidden="true" className="block h-[18px] w-[22px] shrink-0">
    <g transform="translate(0 -2)">
      <path d="M2 15a10 10 0 0 1 20 0" fill="none" stroke="currentColor" strokeOpacity="0.28" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M2 15a10 10 0 0 1 4.2-8.1" fill="none" stroke="currentColor" strokeOpacity={filled >= 3 ? 1 : 0.28} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M6.1 6.9A10 10 0 0 1 17.9 6.9" fill="none" stroke="currentColor" strokeOpacity={filled >= 2 ? 1 : 0.28} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M17.9 6.9A10 10 0 0 1 22 15" fill="none" stroke="currentColor" strokeOpacity={filled >= 1 ? 1 : 0.28} strokeWidth="2.5" strokeLinecap="round" />
      <path
        d={filled === 1 ? 'M12 15 17.5 10.5' : filled === 2 ? 'M12 15 12 7' : 'M12 15 6.5 10.5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="12" cy="15" r="2" fill="currentColor" />
    </g>
  </svg>
);

export const DifficultyBadge = ({
  difficulty,
  isReview = false,
  reviewLabel = 'مرور',
  fontSize,
  className = '',
  guideTargetId,
}: DifficultyBadgeProps) => {
  const config = difficultyConfig[normalizeDifficulty(difficulty)];
  const responsiveFontSize = fontSize
    ? Math.max(9, Math.min(12, fontSize * 0.72))
    : undefined;
  const responsiveIconSize = fontSize
    ? Math.max(15, Math.min(18, fontSize * 1.05))
    : 18;
  const responsiveHeight = fontSize
    ? Math.max(24, Math.min(29, fontSize * 1.65))
    : undefined;

  return (
    <div
      dir="rtl"
      data-guide={guideTargetId}
      className={`inline-flex h-7 min-h-7 w-fit self-end items-center rounded-md border border-white bg-transparent px-1 text-[10px] font-bold leading-none text-white shadow-md backdrop-blur-sm font-persian ${className}`}
      aria-label={isReview ? reviewLabel : `سطح سوال: ${config.label}`}
      style={{
        ...(responsiveFontSize ? { fontSize: `${responsiveFontSize}px` } : {}),
        ...(responsiveHeight ? { height: `${responsiveHeight}px`, minHeight: `${responsiveHeight}px` } : {}),
      }}
    >
      <span className="flex translate-y-px items-center justify-start gap-2">
        {isReview ? <RotateCcw size={responsiveIconSize} className="block shrink-0" /> : <DifficultyIcon filled={config.filled} />}
        <span className="flex h-[18px] shrink-0 items-center whitespace-nowrap leading-[18px]">
          {isReview ? reviewLabel : config.label}
        </span>
      </span>
    </div>
  );
};

export default DifficultyBadge;
